'use client';

import { icons } from '@/Icons';
import ModalWrapper from '@/components/Modals/ModalWrapper/ModalWrapper';
import ds from '@/styles/ds/ds.module.scss';
import { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
interface Props {
	isPerjuryChecked: boolean;
	setIsPerjuryChecked: (v: boolean) => void;
	isCitizenChecked: boolean;
	setIsCitizenChecked: (v: boolean) => void;
	setIsModalOpen: (v: boolean) => void;
}

const ModalContent = ({
	isPerjuryChecked,
	setIsPerjuryChecked,
	isCitizenChecked,
	setIsCitizenChecked,
	setIsModalOpen,
}: Props) => {
	const isFormValid = useMemo(
		() => isPerjuryChecked && isCitizenChecked,
		[isPerjuryChecked, isCitizenChecked]
	);

	return (
		<ModalWrapper onClose={() => setIsModalOpen(false)}>
			<div className="fixed top-1/2 left-1/2 z-50 w-full max-w-[436px] -translate-x-1/2 -translate-y-1/2 flex flex-col items-center bg-tokens-negative p-5 rounded-md border-2 border-tokens-negative shadow-lg text-teal-blue">
				{/* Header */}
				<div className="flex items-center gap-2 w-full mb-3">
					<span className="h-5 w-5 relative">
						<icons.verification />
					</span>
					<p className="text-lg font-extrabold leading-none">Verify Level 1</p>
					<button
						aria-label="Close"
						className="ml-auto h-6 w-6"
						onClick={() => setIsModalOpen(false)}
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							width="24"
							height="24"
							fill="none"
						>
							<path
								d="M19.4566 6.19978C19.9147 5.74167 19.9147 5.00091 19.4566 4.54767C18.9985 4.09444 18.2577 4.08957 17.8045 4.54767L12.0051 10.3471L6.20076 4.5428C5.74265 4.08469 5.00188 4.08469 4.54865 4.5428C4.09542 5.00091 4.09054 5.74167 4.54865 6.19491L10.3481 11.9943L4.54378 17.7987C4.08567 18.2568 4.08567 18.9975 4.54378 19.4508C5.00188 19.904 5.74265 19.9089 6.19588 19.4508L11.9953 13.6513L17.7996 19.4556C18.2577 19.9137 18.9985 19.9137 19.4517 19.4556C19.905 18.9975 19.9098 18.2568 19.4517 17.8035L13.6523 12.0041L19.4566 6.19978Z"
								fill="#1A1E23"
							/>
						</svg>
					</button>
				</div>

				{/* Body */}
				<div className="bg-white flex flex-col gap-4 p-5 items-center rounded-md">
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width="104"
						height="104"
						fill="none"
					>
						<path
							d="M44.9485 13.1636C47.9644 7.53399 56.0361 7.534 59.052 13.1636L97.6907 85.2886C100.546 90.618 96.6848 97.0669 90.6389 97.0669H13.3616C7.31581 97.0668 3.45486 90.6179 6.30983 85.2886L44.9485 13.1636ZM52.0003 76.2671C50.0859 76.2673 48.5337 77.8186 48.5335 79.733C48.5335 81.6475 50.0858 83.1996 52.0003 83.1998C53.9148 83.1998 55.4671 81.6476 55.4671 79.733C55.4668 77.8186 53.9147 76.2671 52.0003 76.2671ZM52.0003 37.6002C49.7912 37.6003 48.0003 39.3911 48.0003 41.6002V69.3335C48.0003 71.5425 49.7913 73.3334 52.0003 73.3335C54.2094 73.3335 56.0002 71.5426 56.0003 69.3335V41.6002C56.0003 39.391 54.2094 37.6002 52.0003 37.6002Z"
							fill="#FB401C"
						/>
					</svg>

					<h1 className="text-2xl font-bold text-center">
						READ THIS CAREFULLY
					</h1>

					<div className="flex flex-col gap-4 w-full">
						<label className="flex items-center space-x-2 cursor-pointer">
							<div className="relative inline-block h-6 w-6">
								<input
									id="perjury"
									type="checkbox"
									className="hidden peer"
									checked={isPerjuryChecked}
									onChange={() => setIsPerjuryChecked(!isPerjuryChecked)}
								/>
								<div className="absolute inset-0 bg-white peer-checked:bg-black rounded border-2 border-black"></div>

								<svg
									className="absolute inset-0 m-auto w-4 h-4 text-white hidden peer-checked:block"
									xmlns="http://www.w3.org/2000/svg"
									width="12"
									height="9"
									viewBox="0 0 12 9"
									fill="none"
								>
									<path
										d="M1.5 5.2L4.35714 8L10.5 1"
										stroke="#F2F3F5"
										strokeWidth="2"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
								</svg>
							</div>

							<span className="font-bold">
								I hereby declare under penalty of perjury that:
							</span>
						</label>

						<ul className="list-disc ml-14 space-y-2">
							<li>
								I am{' '}
								<span className="font-bold underline">
									not a U.S. citizen or resident
								</span>
								.
							</li>
							<li>
								I am{' '}
								<span className="font-bold underline">not using a VPN</span> to
								access this site from the U.S. or any country where prohibited.
							</li>
						</ul>

						<label className="flex items-center gap-2 cursor-pointer">
							<div className="relative inline-block h-6 w-6">
								<input
									id="citizen"
									type="checkbox"
									className="hidden peer"
									checked={isCitizenChecked}
									onChange={() => setIsCitizenChecked(!isCitizenChecked)}
								/>
								<div className="absolute inset-0 bg-white peer-checked:bg-black rounded border-2 border-black"></div>
								<svg
									className="absolute inset-0 m-auto w-4 h-4 text-white hidden peer-checked:block"
									xmlns="http://www.w3.org/2000/svg"
									width="12"
									height="9"
									viewBox="0 0 12 9"
									fill="none"
								>
									<path
										d="M1.5 5.2L4.35714 8L10.5 1"
										stroke="#F2F3F5"
										strokeWidth="2"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
								</svg>
							</div>
							<span className="font-bold">
								I have read and understood this statement.
							</span>
						</label>
					</div>
				</div>

				{/* Footer */}
				<button
					onClick={() => setIsModalOpen(false)}
					disabled={!isFormValid}
					className={`${ds.primary} ${ds.grayBlack} w-full h-10 mt-4`}
				>
					Confirm
				</button>
			</div>
		</ModalWrapper>
	);
};

const VerifyLevel1Modal = (props: Props) => {
	const [mounted, setMounted] = useState(false);

	useEffect(() => {
		setMounted(true);
	}, []);

	if (!mounted || typeof window === 'undefined') return null;

	return ReactDOM.createPortal(<ModalContent {...props} />, document.body);
};

export default VerifyLevel1Modal;
